import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { fmtClock, ymdLocal } from '../lib/format.js';
import { locationKeyFromName, isoMondayOf } from '../lib/schedule.js';
import { mergeWeeksByEmployee, fmtHours } from '../lib/schedule-by-employee.js';
import { wantedFit, FIT_COLOR } from '../lib/weekly-hours.js';

// ─── Schedule → By Employee ────────────────────────────────────────────────
// The whole week, one row per person, across every store (CJ 2026-10-06: "a
// way of viewing the full weeks schedule by employee. I find that I'm looking
// for that regularly"). All Shifts is one store at a time and, on a phone, day
// cards only. Managers and owners; reads the same per-store week endpoint
// All Shifts does, once per store — no new server route.

const STORE_COLORS = ['#1565c0', '#2e7d32', '#ad1457', '#ef6c00', '#6a1b9a', '#00838f'];
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A row with no scheduled times is a clock-in nobody was scheduled for (the
// week endpoint folds Square timecards in). It is shown, marked as such, and
// adds nothing to the scheduled hours.
// `stacked` puts the store under the time — the desktop table's cells, where
// seven side-by-side "6:00a–2:00p JT Connell" columns overflow the page.
function ShiftChip({ s, color, stacked }) {
  const scheduled = Boolean(s.scheduledStart || s.scheduledEnd);
  return (
    <div style={{ display: 'flex', alignItems: stacked ? 'flex-start' : 'center', gap: 5, fontSize: 12, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: 99, background: color, flexShrink: 0, marginTop: stacked ? 4 : 0 }} />
      <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', gap: stacked ? 0 : 5 }}>
        {scheduled
          ? <span style={{ fontWeight: 600 }}>{fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}</span>
          : <span style={{ fontStyle: 'italic', color: '#8d6e00' }}>Not scheduled{s.actualStart ? ` · in ${fmtClock(s.actualStart)}` : ''}</span>}
        <span style={{ color: '#757575', fontSize: stacked ? 10 : 12 }}>{s.store}</span>
      </div>
    </div>
  );
}

function WantedNote({ fit, block }) {
  if (!fit) return null;
  return (
    <span title={fit.detail} data-wanted={fit.state}
      style={{ display: 'block', fontSize: 10, fontWeight: 600, color: FIT_COLOR[fit.state], whiteSpace: 'nowrap', marginTop: block ? 1 : 0 }}>
      {fit.state === 'under' ? '▼ ' : fit.state === 'over' ? '▲ ' : ''}{fit.label}
    </span>
  );
}

export function WeekByEmployeeView({ locations }) {
  const [weekStart, setWeekStart] = useState(() => isoMondayOf(new Date()));
  const [weeks, setWeeks] = useState(null);
  const [failed, setFailed] = useState([]);
  const [store, setStore] = useState('');
  const [query, setQuery] = useState('');
  const [wanted, setWanted] = useState({});   // row key → { min, max }
  const names = (locations || []).map((l) => l.name).filter(Boolean);
  const namesKey = names.join('|');

  // Refreshes in place: the previous week stays on screen until the next one
  // arrives, so paging weeks never collapses the page under you.
  const load = useCallback(async () => {
    const results = await Promise.allSettled(
      names.map((n) => api('GET', `/api/schedule/${locationKeyFromName(n)}/week?week_start=${weekStart}`)),
    );
    setWeeks(results.filter((r) => r.status === 'fulfilled').map((r) => r.value));
    setFailed(names.filter((_, i) => results[i].status === 'rejected'));
  }, [namesKey, weekStart]);
  useEffect(() => { load(); }, [load]);

  // The hours each person said they want (profile → Hours wanted), keyed the
  // way mergeWeeksByEmployee keys its rows: Square id, else Monarch id.
  useEffect(() => {
    api('GET', '/api/users').then((users) => {
      const map = {};
      for (const u of users) {
        if (u.desired_hours_min == null && u.desired_hours_max == null) continue;
        const v = { min: u.desired_hours_min, max: u.desired_hours_max };
        if (u.square_id) map[u.square_id] = v;
        map[`m:${u.id}`] = v;
      }
      setWanted(map);
    }).catch(() => setWanted({}));   // the schedule is still useful without it
  }, []);
  const fitOf = (p) => (p.isOpen || !wanted[p.key] ? null : wantedFit(p.totalMinutes / 60, wanted[p.key].min, wanted[p.key].max));

  const thisMon = isoMondayOf(new Date());
  function shiftWeek(n) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + n * 7);
    setWeekStart(ymdLocal(d));
  }

  const merged = mergeWeeksByEmployee(weeks);
  const stores = (weeks || []).map((w) => w.meta?.locationName).filter(Boolean);
  const colorOf = (name) => STORE_COLORS[Math.max(0, stores.indexOf(name)) % STORE_COLORS.length];
  const q = query.trim().toLowerCase();
  const rows = [...merged.people, ...(merged.open ? [merged.open] : [])]
    .filter((p) => (!store || p.stores.includes(store)) && (!q || p.name.toLowerCase().includes(q)));
  const today = ymdLocal(new Date());
  const days = merged.days.map((ymd) => {
    const d = new Date(ymd + 'T12:00:00Z');
    return { ymd, label: DAY[d.getUTCDay()], num: d.getUTCDate(), isToday: ymd === today };
  });

  return (
    <div className="space-y-3">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(-1)}>← Prev</button>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => setWeekStart(thisMon)} disabled={weekStart === thisMon}>This week</button>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(1)}>Next →</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
          Wk of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        <select value={store} onChange={(e) => setStore(e.target.value)} aria-label="Store"
          style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--rule)', borderRadius: 6, background: '#fff' }}>
          <option value="">All stores</option>
          {stores.map((s) => <option key={s} value={s}>Works at {s}</option>)}
        </select>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a name" aria-label="Find a name"
          style={{ fontSize: 12, padding: '6px 8px', border: '1px solid var(--rule)', borderRadius: 6, width: 130 }} />
        <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 'auto' }}>{rows.filter((r) => !r.isOpen).length} staff</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 11, color: '#757575' }}>
        {stores.map((s) => (
          <span key={s}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: colorOf(s), marginRight: 4 }} />{s}</span>
        ))}
      </div>

      {!weeks && <div className="card text-sm text-gray-500">Loading the week…</div>}
      {failed.length > 0 && (
        <div className="card text-xs" style={{ borderLeft: '4px solid #f9a825' }}>
          Could not load {failed.join(', ')} — those shifts are missing below.
        </div>
      )}
      {weeks && rows.length === 0 && (
        <div className="card text-sm text-gray-500">{merged.people.length === 0 ? 'No shifts scheduled for this week.' : 'Nobody matches.'}</div>
      )}

      {/* Phone: one card per person. */}
      {rows.length > 0 && (
        <div className="grid-mobile-cards" style={{ flexDirection: 'column', gap: 8 }}>
          {rows.map((p) => (
            <div key={p.key || 'open'} className="card" style={{ padding: 12 }} data-employee-week={p.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>{p.name}</span>
                  {p.role && <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 6 }}>{p.role}</span>}
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'right' }}>{fmtHours(p.totalMinutes)}<WantedNote fit={fitOf(p)} block /></span>
              </div>
              {days.map((d) => (
                <div key={d.ymd} style={{ display: 'flex', gap: 8, padding: '4px 0', borderTop: '1px solid #f3f3f3',
                                            background: d.isToday ? '#fffbea' : undefined }}>
                  <div style={{ width: 52, flexShrink: 0, fontSize: 12, fontWeight: d.isToday ? 700 : 500, color: '#555' }}>{d.label} {d.num}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {(p.byDay[d.ymd] || []).length === 0
                      ? <span style={{ fontSize: 12, color: '#cfd8dc' }}>—</span>
                      : p.byDay[d.ymd].map((s, i) => <ShiftChip key={i} s={s} color={colorOf(s.store)} />)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Desktop: people × days. */}
      {rows.length > 0 && (
        <div className="grid-desktop-table card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ position: 'sticky', left: 0, background: '#f5f5f5', padding: '8px 12px', textAlign: 'left',
                             fontSize: 11, color: '#757575', textTransform: 'uppercase', letterSpacing: '0.6px', minWidth: 150 }}>Staff</th>
                {days.map((d) => (
                  <th key={d.ymd} style={{ padding: '8px 6px', minWidth: 96, textAlign: 'left', fontSize: 11, textTransform: 'uppercase',
                                           letterSpacing: '0.6px', color: d.isToday ? '#212121' : '#757575', background: d.isToday ? '#fff3c4' : undefined }}>
                    {d.label} <span style={{ fontWeight: 400 }}>{d.num}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.key || 'open'} style={{ borderTop: '1px solid #f0f0f0' }} data-employee-week={p.name}>
                  <td style={{ position: 'sticky', left: 0, background: '#fff', padding: '8px 12px', minWidth: 150 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, fontStyle: p.isOpen ? 'italic' : undefined }}>{p.name}</div>
                    <div style={{ fontSize: 10, color: '#9e9e9e' }}>{[p.role, fmtHours(p.totalMinutes)].filter(Boolean).join(' · ')}</div>
                    <WantedNote fit={fitOf(p)} />
                  </td>
                  {days.map((d) => (
                    <td key={d.ymd} style={{ padding: 6, verticalAlign: 'top', background: d.isToday ? '#fffbea' : undefined }}>
                      {(p.byDay[d.ymd] || []).length === 0
                        ? <span style={{ color: '#cfd8dc' }}>—</span>
                        : <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {p.byDay[d.ymd].map((s, i) => <ShiftChip key={i} s={s} color={colorOf(s.store)} stacked />)}
                          </div>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="card" style={{ fontSize: 11, color: '#757575', padding: 10 }}>
        Scheduled hours across every store. Staff with no shifts this week are not listed.
      </div>
    </div>
  );
}
